import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_ENDPOINTS, DEFAULT_CID } from '../config/api.config';
import { ApiResponse } from '../models/auth.model';
import { ProductCategory } from '../models/category.model';

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  private readonly jewelryCategoryImages = [
    'assets/images/jewelry/ring-1.jpg',
    'assets/images/jewelry/necklace-1.jpg',
    'assets/images/jewelry/bangle-1.jpg',
    'assets/images/jewelry/earrings-1.jpg',
    'assets/images/jewelry/emerald-ring.jpg',
    'assets/images/jewelry/pendant-1.jpg'
  ];

  constructor(private http: HttpClient) {}

  private normalizeCategory(cat: any, index: number = 0): ProductCategory {
    const name = String(cat.name || '').toLowerCase();
    let fallbackImg = this.jewelryCategoryImages[index % this.jewelryCategoryImages.length];

    if (name.includes('necklace') || name.includes('haar') || name.includes('choker') || name.includes('chain')) {
      fallbackImg = 'assets/images/jewelry/necklace-1.jpg';
    } else if (name.includes('bangle') || name.includes('bracelet') || name.includes('kada')) {
      fallbackImg = 'assets/images/jewelry/bangle-1.jpg';
    } else if (name.includes('earring') || name.includes('jhumka') || name.includes('stud')) {
      fallbackImg = 'assets/images/jewelry/earrings-1.jpg';
    } else if (name.includes('emerald') || name.includes('gemstone') || name.includes('ruby')) {
      fallbackImg = 'assets/images/jewelry/emerald-ring.jpg';
    } else if (name.includes('pendant') || name.includes('locket')) {
      fallbackImg = 'assets/images/jewelry/pendant-1.jpg';
    } else if (name.includes('ring') || name.includes('solitaire') || name.includes('diamond')) {
      fallbackImg = 'assets/images/jewelry/ring-1.jpg';
    } else if (name.includes('silver') || name.includes('gift') || name.includes('coin')) {
      fallbackImg = 'assets/images/jewelry/pendant-1.jpg';
    }

    const hasValidImg = cat.imageUrl && typeof cat.imageUrl === 'string' && cat.imageUrl.trim() && !cat.imageUrl.includes('resource/products');
    const validUrl = cat.url && typeof cat.url === 'string' && cat.url.trim() && !cat.url.includes('resource/products');

    const chosenImg = hasValidImg ? cat.imageUrl : (validUrl ? cat.url : fallbackImg);

    return {
      ...cat,
      imageUrl: chosenImg,
      url: chosenImg
    };
  }

  getAllProductCategory(cid: number | string = DEFAULT_CID): Observable<ApiResponse<ProductCategory[]>> {
    return this.http.get<any>(API_ENDPOINTS.PRODUCT_CATEGORY_ALL(cid)).pipe(
      map(res => {
        const rawList = res?.response || res || [];
        const items: ProductCategory[] = Array.isArray(rawList)
          ? rawList.map((c, i) => this.normalizeCategory(c, i))
          : [];
        return { response: items, status: 200, message: 'Success' } as ApiResponse<ProductCategory[]>;
      }),
      catchError(err => {
        console.warn('getAllProductCategory fetch notice:', err);
        return of({ response: [], status: 200, message: 'Success' } as ApiResponse<ProductCategory[]>);
      })
    );
  }

  getPublicProductCategoryById(id: number | string, cid: number | string = DEFAULT_CID): Observable<ApiResponse<ProductCategory>> {
    return this.http.get<any>(API_ENDPOINTS.PRODUCT_CATEGORY_BY_ID(id, cid)).pipe(
      map(res => {
        const cat = res?.response || res;
        const normalized = cat ? this.normalizeCategory(cat) : cat;
        return { response: normalized, status: 200, message: 'Success' } as ApiResponse<ProductCategory>;
      }),
      catchError(err => {
        console.warn('getPublicProductCategoryById fetch notice:', err);
        // Fallback to searching from category list
        return this.getAllProductCategory(cid).pipe(
          map(listRes => {
            const found = listRes.response?.find(c => String(c.productCategoryId || (c as any).id) === String(id));
            return { response: found as ProductCategory, status: 200, message: 'Success' } as ApiResponse<ProductCategory>;
          })
        );
      })
    );
  }
}

